import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { io } from "socket.io-client";
import { GridLayout, noCompactor } from "react-grid-layout";
import { SatisfactionWidget } from "../../Widgets/SatisfactionWidget";
import { WeatherWidget } from "../../Widgets/WeatherWidget";
import { OperationStatusWidget } from "../../Widgets/OperationStatusWidget";
import { SlaMatchingWidget } from "../../Widgets/SlaMatchingWidget";
import { ServiceStatusWidget } from "../../Widgets/ServiceStatusWidget";
import { MapsWidget } from "../../Widgets/MapsWidget";
import { websocketPort } from "../../../server/websocketPort";
import CustomWidgetRenderer from "../../Widgets/CustomWidgetRenderer";
import UrlWidgetRenderer from "../../Widgets/UrlWidgetRenderer";
import useVideowallMode from "../../../utils/hooks/useVideowallMode";

const freeCompactor = { ...noCompactor, allowOverlap: true };

const PageContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(${props => props.$scaleX || 1}, ${props => props.$scaleY || 1});
  transform-origin: center center;
  width: 3840px;
  height: 2160px;
  background-color: #000000;
  overflow: hidden;
  padding: 0px;
`;

const WaitingMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #333;
  font-size: 32px;
  font-weight: bold;
  text-align: center;
`;

const CanvasContainer = styled.div`
  width: 3840px;
  height: 2160px;
  background: #000;
  padding: 0;
`;

const CanvasItem = styled.div`
  width: 100%;
  height: 100%;
  border: none;
  background: transparent;
  border-radius: 20px;
  overflow: hidden;
  & > * {
    margin: 0 !important;
  }
`;

const DisabledMessage = styled.div`
  color: #444;
  font-size: 16px;
  font-weight: bold;
`;

const ZoneC = () => {
  const [layout, setLayout] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [scale, setScale] = useState({ x: 1, y: 1 });
  const [mapLayerConfig, setMapLayerConfig] = useState({});

  useVideowallMode(7680, 2160);

  useEffect(() => {
    document.title = 'Zone C';
    document.body.style.margin = '0';
    document.body.style.padding = '0';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const calculateScale = () => {
      setScale({ x: window.innerWidth / 3840, y: window.innerHeight / 2160 });
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, []);

  useEffect(() => {
    const socket = io(websocketPort, {
      transports: ['websocket'],
      reconnection: true
    });

    socket.on('videowall_zone_update', (data) => {
      if (!data?.zones?.zoneC) return;
      const zoneData = data.zones.zoneC;

      if (zoneData && Array.isArray(zoneData.layout)) {
        console.log('🎨 Zona C: Updating canvas:', zoneData.layout);
        setLayout(zoneData.layout);
        setEnabled(zoneData.enabled !== false);
        if (data.mapLayerConfig) {
          setMapLayerConfig(data.mapLayerConfig);
        }
      }
    });

    return () => socket.disconnect();
  }, []);

  const renderWidget = (widgetId, widgetMeta) => {
    switch (widgetId) {
      case 'map':
        return <MapsWidget mapOverrides={mapLayerConfig} />;
      case 'satisfaction':
        return <SatisfactionWidget />;
      case 'weather':
        return <WeatherWidget />;
      case 'operation':
        return <OperationStatusWidget />;
      case 'sla':
        return <SlaMatchingWidget />;
      case 'servicestatus':
      case 'semicircle':
      case 'totalavailability':
      case 'opsquality':
        return <ServiceStatusWidget />;
      default:
        if (widgetMeta?.widget?.meta?.type === 'url') {
          return <UrlWidgetRenderer url={widgetMeta.widget.meta.url} />;
        }
        if (widgetMeta?.widget) {
          return <CustomWidgetRenderer widget={widgetMeta.widget} />;
        }
        return <CustomWidgetRenderer widget={{ name: widgetId, class: 'Custom', meta: {} }} />;
    }
  };

  const renderCanvas = () => (
    <CanvasContainer>
      {!enabled ? (
        <DisabledMessage>Optional Zone Disabled</DisabledMessage>
      ) : (
        <GridLayout
          className="layout"
          width={3840}
          layout={layout}
          gridConfig={{
            cols: 12,
            rowHeight: 90,
            maxRows: 24,
            margin: [0, 0],
            containerPadding: [0, 0]
          }}
          compactor={freeCompactor}
          dragConfig={{ enabled: false }}
          resizeConfig={{ enabled: false }}
          autoSize={false}
          style={{ height: 2160 }}
        >
          {layout.map((item) => (
            <div key={item.i || item.widgetId}>
              <CanvasItem>
                {renderWidget(item.widgetId || item.i, item)}
              </CanvasItem>
            </div>
          ))}
        </GridLayout>
      )}
    </CanvasContainer>
  );

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      overflow: 'hidden',
      backgroundColor: '#000',
      margin: 0,
      padding: 0
    }}>
      <PageContainer $scaleX={scale.x} $scaleY={scale.y}>
        {layout.length > 0 ? renderCanvas() : (
          <WaitingMessage>Zona C — Waiting for deploy...</WaitingMessage>
        )}
      </PageContainer>
    </div>
  );
};

export default ZoneC;
